<template>
  <div class="search">
    <div class="search__wrapper" @click="focusInput" :class="isFocus ? 'search__wrapper--focus' : ''">
      <input class="search__input" v-model="inputValue" ref="inputRef" @focus="isFocus = true" @blur="isFocus = false" />
      <button class="search__cross-btn" v-if="inputValue" @click="clearInput">
        <CrossIcon />
      </button>
    </div>
    <div class="search__ldr-block" v-if="isLoading">
      <LoaderCircle :color="'var(--circle-loader)'" :size="32" />
    </div>
    <div class="search__empty-block" v-else-if="inputValue.trim().length > 1 && !searchResults.length && !isLoading">
      <p class="search__empty-txt">По вашему запросу ничего не найдено</p>
    </div>
    <div class="search__scroll-box" :class="{ 'search__scroll-box--top': !isAtTop, 'search__scroll-box--bottom': !isAtBottom }" v-else>
      <ul class="search__output-list" ref="scrollContainer" @scroll="handleScroll">
        <li 
          class="search__output-item" 
          v-for="item in searchResults" 
          :key="item.api_id" 
          @click="(!isChecked(item.api_id)) && toggleCheck(item)"
        >
          <AvatarImage class="search__coin-avatar" :src="item.image" :size="24" />
          <p class="search__output-text">{{ item.name }}</p>
          <CheckboxInput class="search__checkbox" :modelValue="isChecked(item.api_id) || isPending(item.api_id)" :disabled="isChecked(item.api_id)" />
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
  import { useStore } from 'vuex';
  import { ref, watch, onMounted, nextTick } from 'vue';
  import { debounce } from 'lodash';
  import { searchBriefcaseCoins } from '@/Groups/Briefcase/private/api/searchBriefcaseCoins';
  import { useBriefcaseState } from '@/Groups/Briefcase/private/composables/computedValues';
  //-------------------------------
  import CrossIcon from '@/Groups/Briefcase/private/svg/CrossIcon.vue';
  import { AvatarImage, CheckboxInput, LoaderCircle } from '@/Groups/Briefcase/private/imports/externalTemplates';
  //-------------------------------
  const store = useStore();
  //-------------------------------
  onMounted(() => {
    inputRef.value.focus();
  })
  //-------------------------------
  const { assets, pendingAssets } = useBriefcaseState();
  //-------------------------------
  const inputValue = ref('');
  const inputRef = ref(null);
  const isLoading = ref(false);
  const searchResults = ref([]);
  const isFocus = ref(false);
  const scrollContainer = ref(null);
  const isAtTop = ref(true);
  const isAtBottom = ref(false);
  //--------------------------------
  onMounted(async () => {
    await nextTick();
    if (scrollContainer.value) {
      handleScroll({ target: scrollContainer.value });
    }
  });
  //-------------------------------
  //Focus на поле
  const focusInput = () => {
    inputRef.value?.focus();
  };
  //Очистка поля
  const clearInput = () => {
    inputValue.value = '';
    searchResults.value = [];
  };
  //Дебаунс-функция поиска
  const debouncedSearch = debounce(async (query) => {
    const trimmed = query.trim();
    if (trimmed.length <= 1) {
      searchResults.value = [];
      return;
    }
    isLoading.value = true;
    try {
      const result = await searchBriefcaseCoins(query);
      searchResults.value = result.results;
    } catch (error) {
      console.error('Search error:', error);
      const message = error?.message || '';
      if (message.includes('Status: 500')) {
        searchResults.value = [];
      }
    } finally {
      isLoading.value = false;
      await nextTick();
      handleScroll()
    }
  }, 400);
  //Проверка, есть ли элемент в assets
  const isChecked = (id) => {
    return assets.value.some(asset => asset.coin?.api_id === id);
  };
  //Для чекбокса — показывает, была ли выбрана монета пользователем (временное добавление)
  const isPending = (id) => {
    return pendingAssets.value.some(asset => asset.api_id === id);
  };
  //Переключатель чекбокса
  const toggleCheck = (asset) => {
    store.dispatch('Briefcase/togglePendingAsset', asset);
  };
  //Обработчик скролла
  const handleScroll = (e) => {
    const target = e ? e.target : scrollContainer.value;
    if(!target) return;
    const {scrollTop, scrollHeight, clientHeight} = target;
    const tolerance = 1;
    isAtTop.value = scrollTop <= tolerance;
    isAtBottom.value = scrollHeight - (scrollTop + clientHeight) <= tolerance;
  }
  //-------------------------------
  watch(inputValue, (newVal) => {
    debouncedSearch(newVal);
  });
</script>

<style lang="scss" scoped>
  @import "./styles.scss";
</style>